<script setup lang="ts">
import { MAX_DEFAULT_BUDGET, MIN_DEFAULT_BUDGET } from '@/helpers/priceHelper'

import type { BaseFormComponentsBaseInput } from '#build/components'

const { t } = useI18n({ useScope: 'local' })

const { maxBudget = MAX_DEFAULT_BUDGET, minBudget = MIN_DEFAULT_BUDGET } =
  defineProps<{
    maxBudget?: string
    minBudget?: string
  }>()
const emits = defineEmits<{
  'update-budget': [string, string]
}>()

const budgetMinRef = ref<InstanceType<
  typeof BaseFormComponentsBaseInput
> | null>(null)
const budgetMaxRef = ref<InstanceType<
  typeof BaseFormComponentsBaseInput
> | null>(null)

const maxBudgetModel = ref(maxBudget === MAX_DEFAULT_BUDGET ? '' : maxBudget)
const minBudgetModel = ref(minBudget === MIN_DEFAULT_BUDGET ? '' : minBudget)

const updateBudget = () => {
  const max =
    maxBudgetModel.value === '' ? MAX_DEFAULT_BUDGET : maxBudgetModel.value
  const min =
    minBudgetModel.value === '' ? MIN_DEFAULT_BUDGET : minBudgetModel.value

  emits('update-budget', max, min)
}

const clearBudget = (input: 'min' | 'max') => {
  if (input === 'min') {
    minBudgetModel.value = ''
    if (budgetMinRef.value) budgetMinRef.value.inputValue = ''
  } else {
    maxBudgetModel.value = ''
    if (budgetMaxRef.value) budgetMaxRef.value.inputValue = ''
  }
}

watch([() => minBudget, () => maxBudget], ([newMin, newMax]) => {
  if (newMin === MIN_DEFAULT_BUDGET) clearBudget('min')
  if (newMax === MAX_DEFAULT_BUDGET) clearBudget('max')
})

const handleClear = () => {
  clearBudget('min')
  clearBudget('max')
}
</script>

<template>
  <div class="mt-4 flex flex-col gap-4 lg:mt-0">
    <div class="mb-4 font-semibold lg:mb-0 lg:text-md">{{ t('budget') }}</div>
    <div class="grid grid-cols-2 gap-4 md:mx-1">
      <div class="relative">
        <BaseFormComponentsBaseInput
          ref="budgetMinRef"
          v-model="minBudgetModel"
          :label="$t('search.priceFrom')"
          class="!px-2"
          id-form="search-budget"
          input-class="w-full"
          inputmode="numeric"
          name="minBudget"
          type="number"
          wrapper-class="override-input-number w-full"
        />
        <button
          v-if="minBudgetModel"
          class="absolute bottom-[18px] right-1"
          type="button"
          @click="clearBudget('min')"
        >
          <BaseIcon name="close" :size="1" class="text-gray-300" />
        </button>
      </div>
      <div class="relative">
        <BaseFormComponentsBaseInput
          ref="budgetMaxRef"
          v-model="maxBudgetModel"
          :label="$t('search.priceTo')"
          class="!px-2"
          id-form="search-budget"
          input-class="w-full"
          inputmode="numeric"
          name="maxBudget"
          type="number"
          wrapper-class="override-input-number w-full"
        />
        <button
          v-if="maxBudgetModel"
          class="absolute bottom-[18px] right-1"
          type="button"
          @click="clearBudget('max')"
        >
          <BaseIcon name="close" :size="1" class="text-gray-300" />
        </button>
      </div>
    </div>
    <div class="mt-4 flex justify-between lg:mt-2">
      <button
        class="text-md font-bold text-gray-700 lg:text-xs lg:text-gray-500"
        type="button"
        @click="handleClear"
      >
        {{ t('eraseAll') }}
      </button>
      <button
        class="rounded bg-secondary-400 px-4 py-3 font-bold text-white lg:bg-transparent lg:p-0 lg:text-xs lg:text-gray-700"
        type="button"
        @click="updateBudget"
      >
        {{ t('validate') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
:deep(.override-input-number label) {
  @apply font-normal;
}
</style>

<i18n lang="json">
{
  "en": {
    "budget": "Budget for your stay",
    "eraseAll": "Erase all",
    "validate": "Validate"
  },
  "fr": {
    "budget": "Budget pour le séjour",
    "eraseAll": "Tout effacer",
    "validate": "Valider"
  }
}
</i18n>
